import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import getPkce from "oauth-pkce";
import {
  APP_ID,
  BASE_URL,
  BBAUTH_URL,
  CLIENT_ID,
  CLIENT_SECRET,
} from "./config";
import { ErrorResponseType } from "./common/types";
import { redirectToSSO } from "../components/Authentication/utils";

declare module "axios" {
  export interface AxiosRequestConfig {
    _retry?: boolean;
  }
}

const axiosCashJournalClient = axios.create({
  baseURL: `${BASE_URL}`,
  headers: {
    Accept: "application/json",
  },
});

const axiosBBAuthClient = axios.create({
  baseURL: BBAUTH_URL,
  headers: {
    Accept: "application/json",
  },
});

const getRefreshToken = async (refreshToken: string) => {
  try {
    const params = new URLSearchParams({
      refresh_token: refreshToken,
      grant_type: "refresh_token",
      client_id: CLIENT_ID,
      client_secret: CLIENT_SECRET,
    });

    const { data } = await axiosBBAuthClient({
      data: params,
      url: "/token",
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });

    return data;
  } catch (err) {
    localStorage.removeItem("USER_INFO");
    window.location.assign("/");
    throw new Error();
  }
};

const processResponseError = async (error: AxiosError<ErrorResponseType>) => {
  const originalRequest = error.config;

  if (error.response?.status === 401 && !originalRequest?._retry) {
    originalRequest!._retry = true;

    const userInfo: any = JSON.parse(window.localStorage.getItem("USER_INFO") || "");

    if (userInfo) {
      const refreshToken = userInfo.refresh_token;

      const data = await getRefreshToken(refreshToken);

      if (data) {
        window.localStorage.setItem("USER_INFO", JSON.stringify(data));
        window.localStorage.setItem("TOKEN", data.access_token);
        if (originalRequest) {
          return axiosCashJournalClient({
            ...originalRequest,
            headers: {
              ...originalRequest.headers,
              Authorization: `${data.token_type} ${data.access_token}`,
            },
          });
        }
      }
    } else {
      const pkce: { verifier: string, challenge: string } = await new Promise((resolve) => {
        getPkce(43, (errorPKCE, { verifier, challenge }) => {
          if (errorPKCE) throw errorPKCE;
          resolve({ verifier, challenge });
        });
      });
      window.sessionStorage.setItem("CODE_VERIFIER", pkce.verifier);

      redirectToSSO(pkce.challenge);
    }
  }

  return Promise.reject(error);
};

const processRequest = async (config: InternalAxiosRequestConfig) => {
  const userInfo: any = JSON.parse(window.localStorage.getItem("USER_INFO") || "{}");
  const userRole: any = JSON.parse(window.localStorage.getItem("USER_ROLE") || "{}");
  let configObj: any = {};

  if (userInfo?.access_token) {
    // eslint-disable-next-line no-param-reassign
    configObj = {
      ...config,
      headers: {
        ...config.headers,
        Authorization: `Bearer ${userInfo.access_token}`,
        "App-Id": APP_ID,
        "Role-Id": userRole,
      },
    };
  }

  return configObj;
};

axiosCashJournalClient.interceptors.request.use(
  processRequest,
  (error) => Promise.reject(error),
);

axiosCashJournalClient.interceptors.response.use(
  (response) => response,
  processResponseError,
);

export { axiosCashJournalClient, axiosBBAuthClient };
